import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AuthModal from './AuthModal';

const TIER_COLORS = { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700' };

const Navbar = () => {
  const location = useLocation();
  const { user, logoutUser, darkMode, toggleDarkMode } = useAuth();
  const { t } = useLanguage();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [discordAnnouncement, setDiscordAnnouncement] = useState(null);

  useEffect(() => {
    fetch('/api/site-config')
      .then(r => r.json())
      .then(data => { if (data?.discordAnnouncement?.enabled) setDiscordAnnouncement(data.discordAnnouncement); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openAuth = (tab) => { setAuthTab(tab); setShowAuthModal(true); };

  const initials = user ? user.username.slice(0, 2).toUpperCase() : '';
  const isBanned = user?.banned || user?.blocked;

  return (
    <>
      {discordAnnouncement && (
        <div className="discord-announcement-bar">
          <div className="discord-announcement-content">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.053a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
            </svg>
            <span dangerouslySetInnerHTML={{ __html: discordAnnouncement.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            <a
              href={discordAnnouncement.inviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="discord-announcement-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.03.053a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
              </svg>
              {discordAnnouncement.buttonLabel}
            </a>
          </div>
        </div>
      )}
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img src="/logo.png" alt="Fast Boost" className="nav-logo-img" />
            <span>Fast <span className="logo-highlight">Boost</span></span>
          </Link>
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>{t('nav.home')}</Link>
            <Link to="/order-status" className={`nav-link ${location.pathname === '/order-status' ? 'active' : ''}`}>{t('nav.orderStatus')}</Link>
            <Link to="/terms" className={`nav-link ${location.pathname === '/terms' ? 'active' : ''}`}>{t('nav.terms')}</Link>

            <button className="dark-mode-toggle" onClick={toggleDarkMode} title={darkMode ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
              {darkMode ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              )}
            </button>

            {user ? (
              <div className="user-menu" ref={dropdownRef}>
                <button className="user-menu-trigger" onClick={() => setShowDropdown(!showDropdown)}>
                  <div className="user-avatar" style={{ borderColor: user.reseller ? '#a855f7' : (TIER_COLORS[user.loyaltyTier] || '#cd7f32') }}>
                    {user.avatar ? <img src={user.avatar} alt={user.username} /> : <span>{initials}</span>}
                  </div>
                  <span className="user-menu-name">{user.username}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: showDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>

                {showDropdown && (
                  <div className="user-dropdown animate-drop">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar" style={{ borderColor: user.reseller ? '#a855f7' : TIER_COLORS[user.loyaltyTier] }}>
                        {user.avatar ? <img src={user.avatar} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : <span>{initials}</span>}
                      </div>
                      <div>
                        <div className="dropdown-username">{user.username}</div>
                        <div className="dropdown-tier" style={{ color: user.reseller ? '#a855f7' : TIER_COLORS[user.loyaltyTier] }}>
                          {user.reseller ? 'Official Reseller' : (user.loyaltyTier.charAt(0).toUpperCase() + user.loyaltyTier.slice(1) + ' ' + t('dashboard.member'))}
                        </div>
                        {/* Balance shown under name only if > 0 */}
                        {(user.balance ?? 0) > 0 && (
                        <div className="dropdown-balance">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 10H2"/><circle cx="16" cy="14" r="2"/></svg>
                          <span style={{color:'#22c55e',fontWeight:600,fontSize:'0.82rem'}}>${(user.balance ?? 0).toFixed(2)}</span>
                        </div>
                        )}
                      </div>
                    </div>

                    {isBanned && (
                      <div className="dropdown-banned-notice">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                        Account Banned
                      </div>
                    )}

                    <div className="dropdown-divider" />
                    <Link to="/dashboard" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      {t('nav.dashboard')}
                    </Link>
                    <Link to="/dashboard?tab=orders" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      {t('nav.orderHistory')}
                    </Link>
                    <Link to="/topup" className="dropdown-item dropdown-topup" onClick={() => setShowDropdown(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 10H2"/><circle cx="16" cy="14" r="2"/></svg>
                      Top Up Balance
                    </Link>
                    <Link to="/dashboard?tab=settings" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                      {t('nav.settings')}
                    </Link>
                    <Link to="/dashboard?tab=support" className="dropdown-item" onClick={() => setShowDropdown(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      {t('nav.support')}
                    </Link>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item dropdown-logout" onClick={() => { logoutUser(); setShowDropdown(false); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      {t('nav.logOut')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-auth-buttons">
                <button className="btn-nav-login" onClick={() => openAuth('login')}>{t('nav.logIn')}</button>
                <button className="btn-nav-signup" onClick={() => openAuth('signup')}>{t('nav.signUp')}</button>
              </div>
            )}
          </div>
        </div>
      </nav>
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} initialTab={authTab} />
    </>
  );
};

export default Navbar;